import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import client from '../lib/api';
import { getRef, clearRef } from '../lib/referral';

const AuthContext = createContext(null);
const GAME_SESSION_KEY = 'pusubet_game_session_id';
const ACCOUNT_SYNC_INTERVAL_MS = 15000;
const HEARTBEAT_INTERVAL_MS = 30000;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [ready, setReady] = useState(false);

  const persistToken = (t) => {
    if (t) localStorage.setItem('pusubet_token', t);
    else localStorage.removeItem('pusubet_token');
  };

  const refresh = useCallback(async (nextToken) => {
    // 2FA değişikliği eski oturumları geçersiz kılar; yeni jeton ilk isteğe yetişmelidir.
    if (typeof nextToken === 'string' && nextToken) persistToken(nextToken);
    const t = localStorage.getItem('pusubet_token');
    if (!t) { setUser(null); setReady(true); return; }
    try {
      const { data } = await client.get('/auth/me');
      setUser(data.user);
    } catch (e) {
      persistToken(null);
      setUser(null);
    } finally {
      setReady(true);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const userId = user?.id;
  useEffect(() => {
    if (!userId) return undefined;

    const syncAccount = async () => {
      if (document.visibilityState === 'hidden') return;
      try {
        const { data } = await client.get('/auth/me');
        setUser((current) => (
          current?.id === data.user?.id ? { ...current, ...data.user } : current
        ));
      } catch {
        // Arka plan yenileme hatası aktif oturumu kapatmamalı.
      }
    };

    const timer = window.setInterval(syncAccount, ACCOUNT_SYNC_INTERVAL_MS);
    window.addEventListener('focus', syncAccount);
    document.addEventListener('visibilitychange', syncAccount);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener('focus', syncAccount);
      document.removeEventListener('visibilitychange', syncAccount);
    };
  }, [userId]);

  // Sekme arka plandayken bakiye tazelemenin anlamı yok, ama oyuncu hâlâ
  // sitede olabilir: oyunu ayrı sekmede açmış ya da telefonda başka
  // uygulamaya geçmiştir. Yönetim panelindeki online sayacı doğru kalsın
  // diye yalnız canlılık bildiren hafif uç çağrılır.
  useEffect(() => {
    if (!userId) return undefined;

    const beat = () => {
      if (document.visibilityState !== 'hidden') return;
      client.post('/auth/heartbeat').catch(() => {
        // Canlılık bildirimi başarısız olursa oturuma dokunulmaz.
      });
    };

    const timer = window.setInterval(beat, HEARTBEAT_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [userId]);

    // Yalnızca bu sekmenin başlattığı ve sessionStorage'da tuttuğu oturum
    // kurtarılır. Sunucudaki genel "aktif oturum" kapatılırsa başka sekme veya
    // cihazda henüz açılmış oyun yanlışlıkla sonlandırılabilir.
    const recoveredRef = React.useRef(false);
    useEffect(() => {
      if (!user || recoveredRef.current) return;
      recoveredRef.current = true;
      (async () => {
        let sessionId = '';
        try {
          sessionId = sessionStorage.getItem(GAME_SESSION_KEY) || '';
          if (!sessionId) return;
          const { data: res } = await client.post('/games/close', {
            session_id: sessionId,
          });
          if (res.returned > 0) {
            setUser((u) => (u ? { ...u, balance: res.balance } : u));
          }
        } catch { /* kurtarma başarısız olursa sessizce geçilir */ }
        finally {
          try {
            if (sessionStorage.getItem(GAME_SESSION_KEY) === sessionId) {
              sessionStorage.removeItem(GAME_SESSION_KEY);
            }
          } catch { /* depolama erişimi yoksa önemsenmez */ }
        }
      })();
    }, [user]);

  const login = async (username, password) => {
    try {
      const { data } = await client.post('/auth/login', { username, password });
      // 2FA açık hesaplarda token yerine doğrulama oturumu döner
      if (data.two_factor_required || data.registration_verification_required) {
        return {
          ok: false,
          twoFactor: {
            challenge: data.challenge,
            purpose: data.registration_verification_required ? 'register' : 'login',
            emailMasked: data.email_masked,
            phoneMasked: data.phone_masked,
            deliveryChannel: data.delivery_channel,
            deliveryTarget: data.delivery_target || data.phone_masked || data.email_masked,
            codeLength: data.code_length || 6,
          },
        };
      }
      persistToken(data.token);
      setUser(data.user);
      return { ok: true, user: data.user };
    } catch (e) {
      return { ok: false, msg: e.response?.data?.detail || 'Giriş başarısız' };
    }
  };

  const verifyTwoFactor = async (challenge, code, purpose = 'login') => {
    try {
      const endpoint = purpose === 'register'
        ? '/auth/register/verify'
        : '/auth/login/2fa';
      const { data } = await client.post(endpoint, { challenge, code });
      persistToken(data.token);
      setUser(data.user);
      if (data.referred) clearRef();
      return { ok: true, user: data.user };
    } catch (e) {
      return { ok: false, msg: e.response?.data?.detail || 'Doğrulama başarısız' };
    }
  };

  const resendTwoFactor = async (challenge, purpose = 'login') => {
    try {
      const endpoint = purpose === 'register'
        ? '/auth/register/resend'
        : '/auth/login/2fa/resend';
      await client.post(endpoint, { challenge });
      return { ok: true };
    } catch (e) {
      return { ok: false, msg: e.response?.data?.detail || 'Kod gönderilemedi' };
    }
  };

  const register = async (data) => {
    try {
      // ?ref= ile gelen referans kodu varsa kayıt payload'ına eklenir
      const ref = getRef();
      const { data: res } = await client.post('/auth/register', ref ? { ...data, ref } : data);
      if (res.registration_verification_required) {
        return {
          ok: false,
          verification: {
            challenge: res.challenge,
            purpose: 'register',
            emailMasked: res.email_masked,
            phoneMasked: res.phone_masked,
            deliveryChannel: res.delivery_channel,
            deliveryTarget: res.delivery_target || res.phone_masked || res.email_masked,
            codeLength: res.code_length || 6,
          },
        };
      }
      persistToken(res.token);
      setUser(res.user);
      if (res.referred) clearRef();
      return { ok: true, user: res.user, referred: res.referred };
    } catch (e) {
      return { ok: false, msg: e.response?.data?.detail || 'Kayıt başarısız' };
    }
  };

  const logout = async () => {
    try {
      await client.post('/auth/logout');
    } finally {
      persistToken(null);
      setUser(null);
    }
  };

  const setBalance = (bal) => setUser((u) => (u ? { ...u, balance: bal } : u));
  const setWalletBalances = ({ balance, bonus_balance: bonusBalance }) => {
    setUser((u) => (u ? {
      ...u,
      ...(balance != null ? { balance: Number(balance) } : {}),
      ...(bonusBalance != null ? { bonus_balance: Number(bonusBalance) } : {}),
    } : u));
  };

  return (
    <AuthContext.Provider value={{
      user,
      ready,
      login,
      verifyTwoFactor,
      resendTwoFactor,
      register,
      logout,
      refresh,
      setBalance,
      setWalletBalances,
      balance: user?.balance || 0,
      bonusBalance: user?.bonus_balance || 0,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
